import { useEffect, useMemo, useRef, useState } from 'react'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import SearchOutlined from '@mui/icons-material/SearchOutlined'
import { alpha } from '@mui/material/styles'

const MAX_RESULTADOS = 12

// Normaliza para filtrar sin distinguir mayúsculas ni acentos
const RE_DIACRITICOS = new RegExp('[\\u0300-\\u036f]', 'g')
const normalizar = (s = '') =>
  String(s).normalize('NFD').replace(RE_DIACRITICOS, '').toLowerCase()

// Command palette estilo spotlight (⌘K). Todo entra por props.items:
// [{ id, label, group, icon?, keywords?, onSelect }]
export default function CommandPalette({ open, onClose, items = [] }) {
  const [query, setQuery] = useState('')
  const [sel, setSel] = useState(0)
  const listRef = useRef(null)

  // Al abrir: query limpia y selección al inicio
  useEffect(() => {
    if (open) {
      setQuery('')
      setSel(0)
    }
  }, [open])

  useEffect(() => {
    setSel(0)
  }, [query])

  const { grupos, planos } = useMemo(() => {
    const q = normalizar(query.trim())
    const filtrados = q
      ? items.filter((it) =>
          normalizar(`${it.label} ${(it.keywords || []).join(' ')}`).includes(q)
        )
      : items
    const visibles = filtrados.slice(0, MAX_RESULTADOS)
    // Agrupar por group preservando el orden de aparición
    const gs = []
    const idx = new Map()
    visibles.forEach((it) => {
      if (!idx.has(it.group)) {
        idx.set(it.group, gs.length)
        gs.push({ group: it.group, items: [] })
      }
      gs[idx.get(it.group)].items.push(it)
    })
    return { grupos: gs, planos: gs.flatMap((g) => g.items) }
  }, [items, query])

  // Auto-scroll del ítem seleccionado con margen de 8px
  useEffect(() => {
    const list = listRef.current
    const el = list && list.querySelector('[data-cmdk-sel="true"]')
    if (el && list) {
      const r = el.getBoundingClientRect()
      const pr = list.getBoundingClientRect()
      if (r.bottom > pr.bottom) list.scrollTop += r.bottom - pr.bottom + 8
      else if (r.top < pr.top) list.scrollTop -= pr.top - r.top + 8
    }
  }, [sel])

  const ejecutar = (item) => {
    if (item && item.onSelect) item.onSelect()
    onClose()
  }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSel((s) => Math.min(s + 1, planos.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSel((s) => Math.max(s - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      ejecutar(planos[sel])
    }
  }

  let indicePlano = -1

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-label="Búsqueda y comandos"
      slotProps={{
        backdrop: {
          sx: (theme) => ({
            backgroundColor:
              theme.palette.mode === 'light' ? 'rgba(14,21,36,0.34)' : 'rgba(5,7,11,0.62)',
            backdropFilter: 'blur(5px)',
          }),
        },
      }}
    >
      <Box
        onKeyDown={onKeyDown}
        sx={(theme) => ({
          position: 'absolute',
          top: '12vh',
          left: 16,
          right: 16,
          margin: '0 auto',
          width: 640,
          maxWidth: '100%',
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.custom.line2}`,
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 36px 100px rgba(0,0,0,0.55)',
          outline: 'none',
          '@keyframes cmdkIn': {
            from: { transform: 'translateY(-10px) scale(.985)', opacity: 0 },
            to: { transform: 'none', opacity: 1 },
          },
          animation: 'cmdkIn .2s cubic-bezier(.2,.7,.2,1) both',
          '@media (max-width: 860px)': { top: '8vh', left: 12, right: 12 },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        })}
      >
        {/* Cinta hi-vis del tope (4px) */}
        <Box
          aria-hidden="true"
          sx={(theme) => ({
            height: 4,
            background: `repeating-linear-gradient(135deg, ${theme.palette.custom.hiVis} 0 13px, #14161c 13px 21px)`,
          })}
        />

        {/* Fila del input */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '15px 18px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <SearchOutlined sx={(theme) => ({ fontSize: 20, color: theme.palette.custom.inkDim })} />
          <TextField
            autoFocus
            fullWidth
            variant="standard"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar módulo, bombero, carro, citación o acción…"
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: 16, fontWeight: 600 },
            }}
            inputProps={{ 'aria-label': 'Buscar módulo, bombero, carro, citación o acción' }}
          />
          <Box
            sx={(theme) => ({
              fontFamily: theme.typography.fontFamilyMono,
              fontSize: 10,
              color: theme.palette.custom.inkDim,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '6px',
              padding: '3px 7px',
              flex: '0 0 auto',
            })}
          >
            ESC
          </Box>
        </Box>

        {/* Lista de resultados */}
        <Box ref={listRef} sx={{ maxHeight: '52vh', overflowY: 'auto', padding: '8px' }}>
          {planos.length === 0 ? (
            <Box sx={{ padding: '40px 20px', textAlign: 'center', fontSize: 14, color: 'text.secondary' }}>
              Sin resultados para «{query}»
            </Box>
          ) : (
            grupos.map((g) => (
              <Box key={g.group}>
                <Box
                  sx={(theme) => ({
                    fontFamily: theme.typography.fontFamilyMono,
                    fontSize: 9.5,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: theme.palette.custom.inkDim,
                    padding: '11px 12px 6px',
                  })}
                >
                  {g.group}
                </Box>
                {g.items.map((it) => {
                  indicePlano += 1
                  const i = indicePlano
                  const activo = i === sel
                  return (
                    <Box
                      key={it.id}
                      data-cmdk-sel={activo ? 'true' : undefined}
                      onMouseEnter={() => setSel(i)}
                      onClick={() => ejecutar(it)}
                      sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: '13px',
                        padding: '9px 12px',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        backgroundColor: activo
                          ? alpha(theme.palette.primary.main, 0.13)
                          : 'transparent',
                      })}
                    >
                      <Box
                        sx={(theme) => ({
                          width: 34,
                          height: 34,
                          borderRadius: '9px',
                          display: 'grid',
                          placeItems: 'center',
                          flex: '0 0 auto',
                          backgroundColor: activo
                            ? theme.palette.primary.main
                            : theme.palette.custom.bg2,
                          color: activo ? '#fff' : 'text.secondary',
                          '& svg': { fontSize: 17 },
                        })}
                      >
                        {it.icon || null}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {it.label}
                        </Box>
                      </Box>
                      <Box
                        sx={(theme) => ({
                          marginLeft: 'auto',
                          flex: '0 0 auto',
                          fontFamily: theme.typography.fontFamilyMono,
                          fontSize: 11,
                          color: 'text.secondary',
                          opacity: activo ? 0.85 : 0,
                        })}
                      >
                        ↵ Enter
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            ))
          )}
        </Box>

        {/* Footer con hints de teclas */}
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '10px 16px',
            borderTop: '1px solid',
            borderColor: 'divider',
            fontFamily: theme.typography.fontFamilyMono,
            fontSize: 10,
            color: theme.palette.custom.inkDim,
            '& .kbd': {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '5px',
              padding: '1px 6px',
              marginRight: '5px',
            },
          })}
        >
          <span>
            <span className="kbd">↑↓</span>navegar
          </span>
          <span>
            <span className="kbd">↵</span>abrir
          </span>
          <span>
            <span className="kbd">esc</span>cerrar
          </span>
        </Box>
      </Box>
    </Modal>
  )
}
